/**
 * M2 P6+: SAMPLED ship SFX layered over the procedural Web Audio engine. Buffers
 * are loaded + decoded lazily once (after the user-gesture AudioContext resume) and
 * routed through the existing SFX / UI / ambience bus gains, so the Audio settings
 * sliders and mute already control them. If a sample is missing or fails to decode we
 * keep the buffer `null` and the caller falls back to the procedural tone (or silence
 * for the gated cues) — mirroring how musicManager degrades to silence, with NO
 * console error. A single aggregated boot notice lists the absent buffers.
 */

/** One-shot, event-driven SFX (fired on discrete game events, not per-frame). */
export type SfxEvent =
  | 'fire'
  | 'impact'
  | 'shieldHit'
  | 'boost'
  | 'dock'
  // Wave 4 gated cues (assets arrive later; degrade to silence / procedural fallback).
  | 'verifyTruth'
  | 'verifyContradiction'
  | 'cruiseSpool'
  | 'cruiseEngage'
  | 'cruiseDrop'
  | 'deathDriftBed'
  | 'uiTick'
  | 'uiConfirm'
  | 'uiBack'
  | 'uiDenied'
  | 'killConfirm'
  | 'lockAcquiredV2'
  | 'waveInboundV2'
  | 'scoopCapture'
  | 'undockClamp';

/** Looping, start/stop-controlled SFX (supercruise tunnel bed, hull-critical alarm). */
export type SfxLoopId = 'cruiseTunnel' | 'hullCritical';

type SampleBusKey = 'sfx' | 'ui' | 'ambience';
export type SampleBuses = Record<SampleBusKey, GainNode>;

interface SampleConfig {
  stem: string;
  bus: SampleBusKey;
}

const ONESHOT: Record<SfxEvent, SampleConfig> = {
  fire: { stem: 'weapon-fire', bus: 'sfx' },
  impact: { stem: 'impact', bus: 'sfx' },
  shieldHit: { stem: 'shield-hit', bus: 'sfx' },
  boost: { stem: 'boost', bus: 'sfx' },
  dock: { stem: 'dock', bus: 'sfx' },
  verifyTruth: { stem: 'verify-truth', bus: 'ui' },
  verifyContradiction: { stem: 'verify-contradiction', bus: 'ui' },
  cruiseSpool: { stem: 'cruise-spool', bus: 'sfx' },
  cruiseEngage: { stem: 'cruise-engage', bus: 'sfx' },
  cruiseDrop: { stem: 'cruise-drop', bus: 'sfx' },
  deathDriftBed: { stem: 'death-drift-bed', bus: 'ambience' },
  uiTick: { stem: 'ui-tick', bus: 'ui' },
  uiConfirm: { stem: 'ui-confirm', bus: 'ui' },
  uiBack: { stem: 'ui-back', bus: 'ui' },
  uiDenied: { stem: 'ui-denied', bus: 'ui' },
  killConfirm: { stem: 'kill-confirm', bus: 'sfx' },
  lockAcquiredV2: { stem: 'lock-acquired-v2', bus: 'ui' },
  waveInboundV2: { stem: 'wave-inbound-v2', bus: 'ui' },
  scoopCapture: { stem: 'scoop-capture', bus: 'sfx' },
  undockClamp: { stem: 'undock-clamp', bus: 'sfx' },
};

const LOOP: Record<SfxLoopId, SampleConfig> = {
  cruiseTunnel: { stem: 'cruise-tunnel-loop', bus: 'ambience' },
  hullCritical: { stem: 'hull-critical-loop', bus: 'sfx' },
};

const ENGINE_IDLE = 'engine-idle';
const ENGINE_THRUST = 'engine-thrust';

const SFX_BASE = '/assets/audio/sfx';
const ENGINE_RAMP_SEC = 0.12;
/** Named loops (tunnel bed / hull alarm) sit low under the mix — restrained. */
const NAMED_LOOP_GAIN = 0.5;

/** Subtle, restrained — the sampled engine layers OVER the procedural hum. */
export const ENGINE_SAMPLE_GAIN = 0.22;

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));
const sfxUrl = (stem: string): string => `${SFX_BASE}/${stem}.mp3`;

/** Every stem the bank tries to load, in a stable order (for the boot report). */
const ALL_STEMS: readonly string[] = [
  ...Object.values(ONESHOT).map((c) => c.stem),
  ...Object.values(LOOP).map((c) => c.stem),
  ENGINE_IDLE,
  ENGINE_THRUST,
];

export interface EngineMixLevels {
  idle: number;
  thrust: number;
}

/**
 * Pure throttle -> idle/thrust gain blend (equal-power crossfade). At throttle 0 the
 * idle layer is full and thrust is silent; at full throttle thrust dominates. Kept
 * pure + exported for unit testing — `SfxSampleBank.setEngine` inlines the same curve
 * to avoid a per-frame allocation.
 */
export const engineMix = (throttle: number, level = ENGINE_SAMPLE_GAIN): EngineMixLevels => {
  const angle = (clamp01(Math.abs(throttle)) * Math.PI) / 2;
  return { idle: level * Math.cos(angle), thrust: level * Math.sin(angle) };
};

const decodeSample = async (
  ctx: AudioContext,
  url: string,
): Promise<AudioBuffer | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await ctx.decodeAudioData(await response.arrayBuffer());
  } catch {
    return null;
  }
};

interface EngineLoop {
  idleGain: GainNode;
  thrustGain: GainNode;
}

export class SfxSampleBank {
  private readonly buffers = new Map<string, AudioBuffer | null>();
  private engine: EngineLoop | null = null;
  private lastThrottle = 0;
  private engineRequested = false;
  private readonly activeLoops = new Map<SfxLoopId, AudioBufferSourceNode>();

  constructor(
    private readonly ctx: AudioContext,
    private readonly buses: SampleBuses,
    private readonly onReady?: (missing: string[]) => void,
  ) {
    void this.loadAll();
  }

  /** Play a one-shot sample on its configured bus. Returns false when its buffer is
   * absent so the caller can fall back to the procedural tone (or silence). */
  play(event: SfxEvent): boolean {
    const cfg = ONESHOT[event];
    const buffer = this.buffers.get(cfg.stem);
    if (!buffer) return false;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.buses[cfg.bus]);
    source.start();
    return true;
  }

  /** Start a named looping cue (idempotent). Returns false (no-op) when absent. */
  startLoop(id: SfxLoopId): boolean {
    if (this.activeLoops.has(id)) return true;
    const cfg = LOOP[id];
    const buffer = this.buffers.get(cfg.stem);
    if (!buffer) return false;
    const gain = this.ctx.createGain();
    gain.gain.value = NAMED_LOOP_GAIN;
    gain.connect(this.buses[cfg.bus]);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    source.start();
    this.activeLoops.set(id, source);
    return true;
  }

  /** Stop a named looping cue (safe when it is not playing). */
  stopLoop(id: SfxLoopId): void {
    const source = this.activeLoops.get(id);
    if (!source) return;
    this.activeLoops.delete(id);
    try {
      source.stop();
    } catch {
      /* already stopped / ended */
    }
    source.disconnect();
  }

  /** Crossfade the looping idle/thrust layers by throttle. No-op (procedural hum
   * carries the engine) until both engine buffers have decoded. */
  setEngine(throttle: number): void {
    this.lastThrottle = throttle;
    this.engineRequested = true;
    if (!this.engine) return;
    const angle = (clamp01(Math.abs(throttle)) * Math.PI) / 2;
    const now = this.ctx.currentTime;
    this.engine.idleGain.gain.setTargetAtTime(
      ENGINE_SAMPLE_GAIN * Math.cos(angle), now, ENGINE_RAMP_SEC,
    );
    this.engine.thrustGain.gain.setTargetAtTime(
      ENGINE_SAMPLE_GAIN * Math.sin(angle), now, ENGINE_RAMP_SEC,
    );
  }

  private async loadAll(): Promise<void> {
    await Promise.all(ALL_STEMS.map((stem) => this.loadOne(stem)));
    this.tryStartEngine();
    this.onReady?.(ALL_STEMS.filter((stem) => !this.buffers.get(stem)));
  }

  private async loadOne(stem: string): Promise<void> {
    this.buffers.set(stem, await decodeSample(this.ctx, sfxUrl(stem)));
  }

  private tryStartEngine(): void {
    const idle = this.buffers.get(ENGINE_IDLE);
    const thrust = this.buffers.get(ENGINE_THRUST);
    if (!idle || !thrust) return;
    const idleGain = this.startEngineLoop(idle);
    const thrustGain = this.startEngineLoop(thrust);
    this.engine = { idleGain, thrustGain };
    if (this.engineRequested) this.setEngine(this.lastThrottle);
  }

  private startEngineLoop(buffer: AudioBuffer): GainNode {
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    gain.connect(this.buses.ambience);
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    source.start();
    return gain;
  }
}
